import { endOfMonth, isSameMonth, startOfMonth, subMonths } from '@/utils/dates';
import { Currency } from '@seapoint/server/enums';
import { Bar, BarChart, CartesianGrid, Cell, ReferenceArea, XAxis, YAxis } from 'recharts';
import { ChartConfig, ChartContainer, ChartTooltip } from '../../../../design-system/chart/chart';
import { cn } from '../../../../utils/cn';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';

export type MoneyMovementDataPoint = {
  period: string; // Display label (e.g., "19 May", "24 May")
  moneyIn: number; // Positive amount for money coming in
  moneyOut: number; // Positive amount for money going out (will be displayed as negative)
  date: string; // ISO date string for this period
  rawDate?: Date; // Actual Date object
};

type MoneyMovementChartProps = {
  height?: number;
  data: MoneyMovementDataPoint[];
  baseCurrency: Currency;
  className?: string;
  referenceDate?: Date;
  highlightedMonth?: Date;
  onMonthSelect?: (date: Date) => void;
};

const chartConfig = {
  moneyIn: {
    label: 'Money In',
    color: '#A1D9C2',
  },
  moneyOut: {
    label: 'Money Out',
    color: '#F7B8A6',
  },
} satisfies ChartConfig;

// Faded versions (equivalent to 30% opacity on white background) - solid colors so grid doesn't show through
const fadedColors = {
  moneyIn: '#E3F4ED',
  moneyOut: '#FDEAE4',
};

export const MoneyMovementChart = ({
  height = 300,
  data,
  baseCurrency,
  className,
  referenceDate,
  highlightedMonth,
  onMonthSelect,
}: MoneyMovementChartProps) => {
  // Keep all values positive - red color will indicate outgoing amounts
  const chartData = data.map((point) => ({
    ...point,
    moneyOut: Math.abs(point.moneyOut), // Ensure money out is positive for upward bars
    // Use existing rawDate or create new one from date string
    rawDate: point.rawDate || new Date(point.date),
  }));

  // Calculate the range that should be fully opaque (last 4 months ending at referenceDate)
  // This aligns with the table which shows 4 months ending at referenceDate
  const opacityRangeStart = referenceDate ? startOfMonth(subMonths(referenceDate, 3)) : undefined;
  const opacityRangeEnd = referenceDate ? endOfMonth(referenceDate) : undefined;

  const isVisibleInTable = (date: Date) => {
    if (!opacityRangeStart || !opacityRangeEnd) return true;
    return date >= opacityRangeStart && date <= opacityRangeEnd;
  };

  // Find the data point corresponding to the highlighted month for background
  const highlightedDataPoint = chartData.find((d) => highlightedMonth && isSameMonth(d.rawDate, highlightedMonth));

  return (
    <ChartContainer config={chartConfig} className={cn(`h-[${height}px]`, 'w-full', className)}>
      <BarChart
        accessibilityLayer
        data={chartData}
        barCategoryGap="18%" // Percentage-based gap to match table column spacing
        barGap={24} // Gap between bars in the same category (Money In/Out)
        margin={{ top: 20, right: 0, bottom: 20, left: 0 }}
        onClick={(state) => {
          if (state && state.activePayload && state.activePayload.length > 0) {
            const payload = state.activePayload[0].payload as MoneyMovementDataPoint & { rawDate: Date };
            if (payload.rawDate && onMonthSelect) {
              onMonthSelect(payload.rawDate);
            }
          }
        }}
      >
        <CartesianGrid strokeDasharray="4 4" vertical={true} horizontal={true} stroke="#D1D5DB" />

        {highlightedDataPoint && (
          <ReferenceArea
            x1={highlightedDataPoint.period}
            x2={highlightedDataPoint.period}
            y1={0}
            fill="#F9FAFB" // Light gray background for selected month
            fillOpacity={1}
            radius={[8, 8, 0, 0]} // Rounded top corners
            ifOverflow="extendDomain"
          />
        )}

        <XAxis
          dataKey="period"
          tickLine={false}
          axisLine={false}
          tickMargin={16}
          tick={{ fontSize: 12, fill: '#6B7280' }}
          interval={0} // Show all ticks
          padding={{ left: 0, right: 0 }} // Remove default padding for perfect alignment
        />
        <YAxis
          width={90}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickCount={4}
          domain={[0, 'auto']}
          tick={{ fontSize: 12, fill: '#6B7280' }}
          tickFormatter={(value) => {
            const numValue = value as number;
            const currencySymbol = baseCurrency === 'GBP' ? '£' : '€';

            if (numValue === 0) {
              return `${currencySymbol}0`;
            }

            return `${currencySymbol}${numValue.toLocaleString('en-US')}`;
          }}
        />
        <ChartTooltip
          cursor={{ fill: '#F3F4F6', opacity: 0.4 }}
          content={({ active, payload, label }) => {
            if (active && payload && payload.length > 0) {
              const originalDataPoint = payload[0]?.payload as MoneyMovementDataPoint;
              if (!originalDataPoint) return null;

              const moneyIn = originalDataPoint.moneyIn;
              const moneyOut = originalDataPoint.moneyOut;

              const formattedMoneyIn = getAmountWithCurrency({
                amount: moneyIn,
                currency: baseCurrency,
                isAccountBalance: true,
              });

              const formattedMoneyOut = getAmountWithCurrency({
                amount: moneyOut,
                currency: baseCurrency,
                isAccountBalance: true,
              });

              return (
                <div className="rounded-lg border border-border bg-white p-3 shadow-lg">
                  <div className="grid gap-2">
                    <div className="text-sm font-medium text-gray-900">{label}</div>
                    <div className="grid gap-1.5 text-xs">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: chartConfig.moneyIn.color }}
                          />
                          <span className="text-muted-foreground">Money In</span>
                        </div>
                        <span className="font-medium text-success tabular-nums">{formattedMoneyIn}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: chartConfig.moneyOut.color }}
                          />
                          <span className="text-muted-foreground">Money Out</span>
                        </div>
                        <span className="font-medium text-destructive tabular-nums">-{formattedMoneyOut}</span>
                      </div>
                      <div className="mt-1 border-t border-gray-100 pt-1.5">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium text-muted-foreground">Net</span>
                          <span
                            className={cn(
                              'font-medium tabular-nums',
                              moneyIn - moneyOut >= 0 ? 'text-success' : 'text-destructive',
                            )}
                          >
                            {getAmountWithCurrency({
                              amount: moneyIn - moneyOut,
                              currency: baseCurrency,
                              isAccountBalance: true,
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />
        <Bar dataKey="moneyIn" radius={[2, 2, 0, 0]} barSize={16} cursor="pointer">
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-in-${index}`}
              fill={isVisibleInTable(entry.rawDate) ? chartConfig.moneyIn.color : fadedColors.moneyIn}
            />
          ))}
        </Bar>
        <Bar dataKey="moneyOut" radius={[2, 2, 0, 0]} barSize={16} cursor="pointer">
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-out-${index}`}
              fill={isVisibleInTable(entry.rawDate) ? chartConfig.moneyOut.color : fadedColors.moneyOut}
            />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
};
